import { useRef, useState } from 'react';
import { X, Upload, Film, Loader2, AlertCircle, CheckCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import { storageService } from '@/services/storage.service';
import { courseService } from '@/services/course.service';
import { UploadProgress } from '@/types/storage.types';

interface UploadContentVideoModalProps {
  isOpen: boolean;
  contentItemId: string;
  contentItemTitle?: string;
  onClose: () => void;
  onSuccess: () => void;
}

const ACCEPT = '.mp4,.avi,.mov,video/mp4,video/x-msvideo,video/quicktime';
const ALLOWED_EXT = ['mp4', 'avi', 'mov'];
// Matches the server gate in TenantBoundaryEnforcer, which rejects anything over
// 50 MB on /storage/utils/upload - promising more here just fails later.
const MAX_SIZE_MB = 50;
const MAX_SIZE_BYTES = MAX_SIZE_MB * 1024 * 1024;

/**
 * Upload a single video (MP4/AVI/MOV) directly into a scene-less
 * video_presentation content item. The raw file goes to S3 via the storage
 * service; the backend then sets it as the item final video (no scene merge)
 * and records its duration. AVI/MOV are transcoded to MP4 on the backend for
 * browser playback. Max 50 MB.
 */
export default function UploadContentVideoModal({
  isOpen,
  contentItemId,
  contentItemTitle,
  onClose,
  onSuccess,
}: UploadContentVideoModalProps) {
  const [file, setFile] = useState<File | null>(null);
  const [durationSeconds, setDurationSeconds] = useState<number>(0);
  const [isUploading, setIsUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<string>('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Read the video duration client-side so the backend can set the item's
  // estimated time from it. Best-effort — an unreadable duration just stays 0.
  const readDuration = (f: File): Promise<number> =>
    new Promise((resolve) => {
      try {
        const url = URL.createObjectURL(f);
        const video = document.createElement('video');
        video.preload = 'metadata';
        video.onloadedmetadata = () => {
          URL.revokeObjectURL(url);
          resolve(Number.isFinite(video.duration) ? Math.round(video.duration) : 0);
        };
        video.onerror = () => {
          URL.revokeObjectURL(url);
          resolve(0);
        };
        video.src = url;
      } catch {
        resolve(0);
      }
    });

  const pickFile = async (f: File | undefined | null) => {
    if (!f) return;
    const ext = f.name.split('.').pop()?.toLowerCase() || '';
    if (!ALLOWED_EXT.includes(ext)) {
      const msg = 'Only MP4, AVI or MOV videos are allowed';
      setError(msg);
      toast.error(msg);
      return;
    }
    if (f.size > MAX_SIZE_BYTES) {
      const msg = `Video is too large (${storageService.formatFileSize(f.size)}). Maximum size is ${MAX_SIZE_MB} MB.`;
      setError(msg);
      toast.error(msg);
      // Reset the input so re-selecting the same oversized file re-triggers onChange.
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }
    setError('');
    setFile(f);
    setDurationSeconds(await readDuration(f));
  };

  const handleClose = () => {
    if (isUploading) {
      toast.error('Please wait for the upload to complete');
      return;
    }
    setFile(null);
    setDurationSeconds(0);
    setProgress(0);
    setError('');
    onClose();
  };

  const handleUpload = async () => {
    if (!file) {
      toast.error('Please select a video');
      return;
    }
    // Defensive re-check so an oversized file can never reach S3.
    if (file.size > MAX_SIZE_BYTES) {
      toast.error(`Video is too large. Maximum size is ${MAX_SIZE_MB} MB.`);
      return;
    }
    setIsUploading(true);
    setProgress(0);
    // AVI/MOV are transcoded to MP4 on the backend — that takes longer.
    const ext = file.name.split('.').pop()?.toLowerCase() || '';
    const needsConvert = ext !== 'mp4';
    const loading = toast.loading(needsConvert ? 'Uploading & converting video…' : 'Uploading video...');
    try {
      // 1) Upload the raw file to S3.
      const uploaded = await storageService.uploadFile(file, {
        onProgress: (p: UploadProgress) => setProgress(p.percentage),
      });
      const fileUrl = uploaded.url || (uploaded as any).file_path || '';
      const fileKey = uploaded.key || (uploaded as any).file_path || '';
      const fileType = uploaded.fileType || file.type || 'video/mp4';

      // 2) Ask the backend to attach it as the content item's video.
      await courseService.uploadContentItemVideo(contentItemId, {
        file_url: fileUrl,
        file_key: fileKey,
        file_type: fileType,
        file_name: uploaded.name || file.name,
        file_size: file.size,
        duration_seconds: durationSeconds || undefined,
      });

      toast.success('Video uploaded and added to the content item', { id: loading });
      setFile(null);
      setDurationSeconds(0);
      setProgress(0);
      onSuccess();
      onClose();
    } catch (error: any) {
      toast.error(error?.message || 'Failed to upload the video', { id: loading });
    } finally {
      setIsUploading(false);
    }
  };

  if (!isOpen) return null;

  const formatDuration = (s: number) => {
    if (!s) return '';
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return `${m}:${String(sec).padStart(2, '0')}`;
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4" dir="ltr">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg flex flex-col">
        {/* Header */}
        <div className="p-5 border-b flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Upload a Video</h2>
            <p className="text-sm text-gray-500 mt-0.5">
              {contentItemTitle ? `Into "${contentItemTitle}"` : 'MP4/AVI/MOV — set as the content video'}
            </p>
          </div>
          <button onClick={handleClose} disabled={isUploading} className="p-1 rounded-full hover:bg-gray-100 disabled:opacity-50">
            <X size={22} />
          </button>
        </div>

        {/* Body */}
        <div className="p-5">
          {/* Inline block message (e.g. file too large / wrong type) */}
          {error && (
            <div className="mb-4 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2.5 text-sm text-red-700">
              <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}
          {!file ? (
            <div
              onDrop={(e) => { e.preventDefault(); pickFile(e.dataTransfer.files?.[0]); }}
              onDragOver={(e) => e.preventDefault()}
              className={`border-2 border-dashed rounded-xl p-8 text-center transition-colors ${error ? 'border-red-300 hover:border-red-400' : 'border-gray-300 hover:border-brand-indigo-400'}`}
            >
              <Upload className="w-10 h-10 text-gray-400 mx-auto mb-3" />
              <p className="text-sm font-medium text-gray-800 mb-1">Drop a video here or click to browse</p>
              <p className="text-xs text-gray-500 mb-4">MP4, AVI or MOV · max {MAX_SIZE_MB} MB</p>
              <input
                ref={fileInputRef}
                type="file"
                accept={ACCEPT}
                className="hidden"
                onChange={(e) => pickFile(e.target.files?.[0])}
              />
              <button onClick={() => fileInputRef.current?.click()} className="btn btn-primary">
                Select Video
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
              <Film className="w-5 h-5 text-gray-500 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">{file.name}</p>
                <p className="text-xs text-gray-500">
                  {storageService.formatFileSize(file.size)}
                  {durationSeconds > 0 && ` · ${formatDuration(durationSeconds)}`}
                </p>
                {isUploading && (
                  <div className="bg-gray-200 rounded-full h-1.5 mt-2">
                    <div className="bg-brand-indigo-500 h-1.5 rounded-full transition-all" style={{ width: `${progress}%` }} />
                  </div>
                )}
              </div>
              {isUploading ? (
                <Loader2 className="w-5 h-5 text-brand-indigo-500 animate-spin" />
              ) : progress === 100 ? (
                <CheckCircle className="w-5 h-5 text-green-500" />
              ) : (
                <button onClick={() => { setFile(null); setDurationSeconds(0); }} className="text-gray-400 hover:text-red-600">
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
          )}

          <div className="flex items-start gap-2 mt-4 text-xs text-gray-500">
            <AlertCircle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
            <span>The uploaded video becomes the content video directly — no scenes are used. AVI/MOV files are converted to MP4 (may take a little longer). You can remove it later to go back to scenes.</span>
          </div>
        </div>

        {/* Footer */}
        <div className="p-4 bg-gray-50 rounded-b-xl flex justify-end gap-3">
          <button onClick={handleClose} disabled={isUploading} className="btn btn-secondary">
            Cancel
          </button>
          <button onClick={handleUpload} disabled={!file || isUploading} className="btn btn-primary inline-flex items-center gap-2">
            {isUploading ? (<><Loader2 className="w-4 h-4 animate-spin" /> Uploading…</>) : (<><Upload className="w-4 h-4" /> Upload</>)}
          </button>
        </div>
      </div>
    </div>
  );
}
